import React from 'react';
import { StatisticsLayout } from 'views/components';
import isEmpty from 'lodash/isEmpty';
import { statisticsActions } from 'core/Statistics';
import { useTranslation } from 'react-i18next';
import {
    Card,
    Container,
    InfoCard,
    PieChart
} from 'mol-ui';
import {
    Col,
    Row
} from 'reactstrap';
import {
    isLocked,
    objectToChart,
    parseRecipeRequestStatus
} from 'shared/utils';
import {
    useDispatch,
    useSelector,
    useWindowSize
} from 'hooks';

const StatisticsRecipes = () => {
    
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { xs } = useWindowSize();
    const { user } = useSelector('account');
    const { configurations } = useSelector('config');
    const {
        loading,
        recipeRequests: {
            status,
            total,
            validations,
            validation_status
        } = {},
        recipeRequestsDownload,
        recipeRequestsDownloading
    } = useSelector('statistics');
    const hasOnlineValidations = !!configurations?.integrations?.includes('OSPEPRI');

    const onFiltersChange = filters => {
        dispatch(statisticsActions.getStatisticsRecipeRequests(filters));
    };

    const downloadHeaders = [{
        key: 'created_at',
        label: t('Fecha')
    }, {
        key: 'patient_identification',
        label: t('DNIPaciente')
    }, {
        key: 'patient_name',
        label: t('Paciente')
    }, {
        key: 'doctor_identification',
        label: t('DNI')
    }, {
        key: 'doctor_name',
        label: t('Profesional')
    }, {
        key: 'subject',
        label: t('Asunto')
    }, {
        key: 'description',
        label: t('Contenido')
    }, {
        key: 'doctor_observation',
        label: t('Observaciones profesionales')
    }, {
        display: hasOnlineValidations,
        key: 'api_status',
        label: t('Validación')
    }, {
        display: hasOnlineValidations,
        key: 'api_error_message',
        label: t('Error de validación')
    }].filter(({ display = true }) => Boolean(display));

    const downloadController = {
        data: recipeRequestsDownload,
        downloading: recipeRequestsDownloading,
        fileName: t('Solicitudes de receta'),
        headers: downloadHeaders,
        onDownload: filters => dispatch(statisticsActions.getStatisticsRecipeRequestsDownload(filters))
    };

    const statusConfig = {
        config: [{ dataKey: 'value', fontSize: xs ? '11px' : '12px', label: t('Estado') }],
        data: objectToChart(status ?? {}, parseRecipeRequestStatus),
        height: 300,
        minWidth: 400
    };

    const validationStatusConfig = {
        config: [{ dataKey: 'value', fontSize: xs ? '11px' : '12px', label: t('Validaciones') }],
        data: objectToChart(validation_status ?? {}, parseRecipeRequestStatus),
        height: 300,
        minWidth: 600
    };

    return (
        <StatisticsLayout
            downloadController={ !isLocked(user, 'download_reports') ? downloadController : undefined }
            loading={ loading }
            title={ t('Solicitudes de receta') }
            onFiltersChange={ onFiltersChange }
        >
            <Container>
                <Row>
                    <Col md="6" xs="12">
                        <InfoCard
                            caption={ t('Cantidad de solicitudes') }
                            icon="summarize"
                            title={ (total ?? 0).toString() }
                        />
                    </Col>
                    {
                        !isEmpty(status) &&
                        <Col md="6" xs="12">
                            <Card overflowX="auto" title={ t('Estados') }>
                                <PieChart { ...statusConfig } />
                            </Card>
                        </Col>
                    }
                    {
                        hasOnlineValidations &&
                        <>
                            <Col md="6" xs="12">
                                <InfoCard
                                    caption={ t('Cantidad de validaciones') }
                                    icon="done"
                                    title={ (validations ?? 0).toString() }
                                />
                            </Col>
                            {
                                !isEmpty(validation_status) &&
                                <Col md="6" xs="12">
                                    <Card overflowX="auto" title={ t('Validaciones') }>
                                        <PieChart { ...validationStatusConfig } />
                                    </Card>
                                </Col>
                            }
                        </>
                    }
                </Row>
            </Container>
        </StatisticsLayout>
    );
};

export default StatisticsRecipes;
